<template>
  <template v-if="!useRedesign">
    <div class="providers-list">
    <TokuAutodebitBanner v-if="tokuUrl" :url="tokuUrl" />
    <h1 class="listing-titles">
      {{ t("PaymentsProvidersView-HowToPay") }}
    </h1>
    <template v-if="filteredProviders[0].length">
      <ul>
        <li v-for="provider in filteredProviders[0]" :key="provider.name">
          <CardItem
            :logo-src="provider.logo"
            :logo-alt="provider.displayName"
            :title="t(provider.displayName)"
            :subtitle="provider.tooltipPrefix ? t(provider.tooltipPrefix) : ''"
            :warning-text="formatLimitWarning(provider)"
            :right-text="computeRightText(provider)"
            :inactive="
              provider.featureFlagStatus?.disabled === true ||
              (country === 'PE' && isOtherAmountSelected && !otherAmountAllowedProviders.includes(provider.name)) ||
              !!getLimitWarning(provider)
            "
            @click="handleSelectPaymentProvider(provider)"
          />
        </li>
        <li v-if="tokuUrl" data-testid="toku-autodebit-channel">
          <CardItem
            :logo-src="addCardIcon"
            :logo-alt="t('TokuAutodebit-Channel-Title')"
            :title="t('TokuAutodebit-Channel-Title')"
            :right-text="t('no-fee')"
            @click="openTokuUrl"
          />
        </li>
      </ul>
    </template>
    <template v-if="filteredProviders[1].length">
      <ul>
        <li v-for="provider in filteredProviders[1]" :key="provider.name">
          <CardItem
            :logo-src="provider.logo"
            :logo-alt="provider.displayName"
            :title="t(provider.displayName)"
            :subtitle="provider.tooltipPrefix ? t(provider.tooltipPrefix) : ''"
            :warning-text="formatLimitWarning(provider)"
            :right-text="computeRightText(provider)"
            :inactive="
              provider.featureFlagStatus?.disabled === true ||
              (country === 'PE' && isOtherAmountSelected && !otherAmountAllowedProviders.includes(provider.name)) ||
              !!getLimitWarning(provider)
            "
            @click="handleSelectPaymentProvider(provider)"
          />
        </li>
      </ul>
    </template>
  </div>
  <BottomBar
    :text="t('PaymentsProvidersView-PaymentAmount')"
    :amount="amount"
    :term-label="term"
    @change-payment-amount="handleAmountChange"
  />
  <HighlightPopup :is-visible="onHighlighted" @close-popup="onClosePopup('highLight')" @dismiss-popup="onDismissPopup">
    <h1>{{ t("Popup-Pay-Without-Commissions") }}</h1>
    <h2>{{ t("Popup-Save-Money-Using-These-Options") }}</h2>
    <ul class="savings-container">
      <li v-for="provider in highlightedProviders" :key="t(provider.name)" class="savings">
        <img :src="provider.logo" :alt="provider.displayName" />
      </li>
    </ul>
  </HighlightPopup>

  <HighlightPopup
    v-if="pixProvider"
    id="boletoPopup"
    :is-visible="showBoletoPopup"
    :bottom-link-text="t('try-with-pix')"
    :button-text="boletoPopupButtonText"
    @bottom-link-click="tryPix()"
    @close-popup="onClosePopup('boletoPopup')"
    @dismiss-popup="onDismissPopup"
  >
    <img src="@/assets/icons/warn.svg" alt="Warning" />
    <h1 class="text-black">
      {{ boletoPopupTitle }}
    </h1>
    <h2>
      {{ boletoPopupDescription }}
    </h2>
  </HighlightPopup>

  <HighlightPopup
    :is-visible="detailsPopupObject !== undefined"
    :button-text="
      detailsPopupObject?.disabled == true ? (showExtensionCta ? t('use-another-method') : t('close')) : t('continue')
    "
    :bottom-link-text="
      showExtensionCta ? t('ext-entry-option-subtitle') : detailsPopupObject?.disabled == true ? undefined : t('cancel')
    "
    :bottom-link-highlight="showExtensionCta"
    :bottom-link-test-id="showExtensionCta ? 'request-extension-button' : ''"
    :add-close-icon="showExtensionCta"
    @close-popup="onClosePopup('details')"
    @dismiss-popup="onDismissPopup"
    @dismiss-popup-close-btn="onDismissPopup"
    @bottom-link-click="onDetailsBottomLink"
  >
    <div>
      <div><img src="@/assets/icons/warn.svg" height="67" alt="Info" /></div>
      <h1>{{ detailsPopupObject?.title }}</h1>
      <p>{{ detailsPopupObject?.banner }}</p>
    </div>
  </HighlightPopup>

  <HighlightPopup
    :is-visible="
      !!providers.globalWarning.value?.title && !!providers.globalWarning.value?.banner && !globalWarningDismissed
    "
    :button-text="t('close')"
    :bottom-link-text="globalWarningExtensionCta ? t('ext-entry-option-subtitle') : undefined"
    :bottom-link-highlight="globalWarningExtensionCta"
    :bottom-link-test-id="globalWarningExtensionCta ? 'request-extension-button-country' : ''"
    @close-popup="dismissGlobalWarning"
    @dismiss-popup="dismissGlobalWarning"
    @bottom-link-click="onGlobalWarningExtension"
  >
    <div>
      <div><img src="@/assets/icons/warn.svg" height="67" alt="Info" /></div>
      <h1>{{ providers.globalWarning.value?.title }}</h1>
      <p>{{ providers.globalWarning.value?.banner }}</p>
    </div>
  </HighlightPopup>

  <HighlightPopup
    :is-visible="showOtherAmountPopup"
    :button-text="t('close')"
    @close-popup="onDismissPopup"
    @dismiss-popup="onDismissPopup"
  >
    <div>
      <div><img src="@/assets/icons/warn.svg" height="67" alt="Info" /></div>
      <h1>{{ t("attention") }}</h1>
      <p>{{ t("other-amount-qr-content") }}</p>
    </div>
  </HighlightPopup>
  </template>

  <template v-else>
    <V1ScreenScaffold
      data-testid="sp-providers"
      :next-disabled="!canContinueRedesign"
      @next="onRedesignContinue"
    >
      <template #subtitle>
        <ProviderAmountSummary
          v-if="superPacmanInstallment"
          :label="t('PaymentsProvidersView-PaymentAmount')"
          :amount="redesignAmountDisplay"
          :next-payment="redesignNextPayment"
          test-id="sp-providers-amount"
          next-payment-test-id="sp-providers-next-payment"
        />
      </template>
      <TokuAutodebitBanner v-if="tokuUrl" :url="tokuUrl" />
      <h1 class="sp-providers__title" data-testid="sp-providers-title">
        {{ t("PaymentsProvidersView-SelectHowToPay") }}
      </h1>
      <ProviderCardList
        :items="redesignCardItems"
        :selected-key="selectedProviderKey"
        @select="handleRedesignSelect"
      />
    </V1ScreenScaffold>

    <Modal
      :model-value="detailsPopupObject !== undefined"
      placement="bottom"
      :title="detailsPopupObject?.title"
      :primary-text="
        detailsPopupObject?.disabled == true
          ? showExtensionCta
            ? t('use-another-method')
            : t('close')
          : t('continue')
      "
      :secondary-text="
        showExtensionCta
          ? t('ext-entry-option-subtitle')
          : detailsPopupObject?.disabled == true
            ? undefined
            : t('cancel')
      "
      @primary="onRedesignDetailsPrimary"
      @secondary="onDetailsBottomLink"
      @update:model-value="onDismissPopup"
    >
      <p data-testid="sp-provider-popup">{{ detailsPopupObject?.banner }}</p>
    </Modal>

    <Modal
      :model-value="limitPopupProvider !== undefined"
      placement="bottom"
      :title="limitPopupTitle"
      :primary-text="t('close')"
      @primary="onRedesignLimitClose"
      @update:model-value="onRedesignLimitClose"
    >
      <p data-testid="sp-provider-limit-popup">{{ limitPopupBody }}</p>
    </Modal>

    <Modal
      :model-value="
        !!providers.globalWarning.value?.title && !!providers.globalWarning.value?.banner && !globalWarningDismissed
      "
      placement="bottom"
      :title="providers.globalWarning.value?.title"
      :primary-text="t('close')"
      :secondary-text="globalWarningExtensionCta ? t('ext-entry-option-subtitle') : undefined"
      @primary="dismissGlobalWarning"
      @secondary="onGlobalWarningExtension"
      @update:model-value="dismissGlobalWarning"
    >
      <p data-testid="sp-country-notice-popup">{{ providers.globalWarning.value?.banner }}</p>
    </Modal>

    <Modal
      :model-value="onHighlighted"
      placement="bottom"
      :title="t('Popup-Pay-Without-Commissions')"
      :primary-text="t('close')"
      @primary="onClosePopup('highLight')"
      @update:model-value="onDismissPopup"
    >
      <p data-testid="sp-commissions-popup">{{ t('Popup-Save-Money-Using-These-Options') }}</p>
    </Modal>

    <Modal
      :model-value="showOtherAmountPopup"
      placement="bottom"
      :title="t('attention')"
      :primary-text="t('close')"
      @primary="onDismissPopup"
      @update:model-value="onDismissPopup"
    >
      <p data-testid="sp-other-amount-popup">{{ t('other-amount-qr-content') }}</p>
    </Modal>

    <Modal
      v-if="pixProvider"
      :model-value="showBoletoPopup"
      placement="bottom"
      :title="boletoPopupTitle"
      :primary-text="boletoPopupButtonText"
      :secondary-text="t('try-with-pix')"
      @primary="onClosePopup('boletoPopup')"
      @secondary="tryPix()"
      @update:model-value="onDismissPopup"
    >
      <p data-testid="sp-boleto-popup">{{ boletoPopupDescription }}</p>
    </Modal>
  </template>
</template>

<script lang="ts">
import { computed, defineComponent, ref, Ref, UnwrapRef } from "vue";
import TokuAutodebitBanner from "@/components/TokuAutodebitBanner.vue";
import {
  buildTokuUrl,
  getBelowMinimumProviderLimit,
  getExceededProviderLimit,
  providerKeyBuilder,
} from "@/helpers/provider.helpers";
import { usePaymentProviders } from "@/services/DataService";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import router from "@/router";
import CardItem from "@/components/CardItem.vue";
import BottomBar from "@/components/BottomBar.vue";
import { AnalyticEventsNames } from "@/helpers/google-analytics.helpers";
import HighlightPopup from "@/components/HighlightPopup.vue";
import { IInstallmentDetail } from "@/types/IInstallment";
import { getCookie, setCookie } from "@/helpers/cookieHelper";
import authHelper from "@/helpers/authHelper";
import { ActionLogTypeConst } from "@/types/IActionLogs";
import prefetchHelper from "@/helpers/prefetchHelper";
import { Countries } from "@/types/Countries";
import { Providers } from "@/types/Providers";
import { computeTermLabel } from "@/shared-helpers/language.helper";
import { getFallbackLocale } from "@/i18n";
import { IFeatureFlagLogType, IGlobalWarning, IPaymentProvider, IPaymentProviderType } from "@/types/IPaymentProvider";
import { FinanceProductTypes, CustomerNationalIdTypes } from "@/types/ISuperPacman";
import addCardIcon from "@/assets/icons/add_card.svg";
import bankTransferTile from "@/assets/icons/providers/provider_bank_transfer.svg";
import cashTile from "@/assets/icons/providers/provider_cash.svg";
import payjoyStoresTile from "@/assets/icons/providers/provider_payjoy_stores.svg";
import { isUIFlagEnabled, PAYMENT_FLOW_REDESIGN_FLAG } from "@/helpers/uiFlags";
import { Modal } from "@payjoy/design-system";
import V1ScreenScaffold from "@/components/v1/V1ScreenScaffold.vue";
import ProviderAmountSummary from "@/components/providers-redesign/ProviderAmountSummary.vue";
import ProviderCardList, { ProviderCardItem } from "@/components/providers-redesign/ProviderCardList.vue";
import { nextValidThroughMs } from "@/helpers/nextPayment.helpers";

const TOKU_CARD_KEY = "toku-entry";

export default defineComponent({
  name: "PaymentProvidersView",
  components: {
    BottomBar,
    CardItem,
    HighlightPopup,
    TokuAutodebitBanner,
    Modal,
    V1ScreenScaffold,
    ProviderAmountSummary,
    ProviderCardList,
  },
  props: {
    usingSP: {
      type: String,
      required: false,
      default: "false",
    },
  },
  emits: ["select-payment-provider", "change-payment-amount", "close-popup"],
  setup() {
    const { t, te, n, d, locale } = useI18n();
    const router = useRouter();

    const store = useStore();
    const country = computed(() => store.state.payOptions.deviceInfo.country);
    const deviceTag = computed(() => store.state.payOptions.deviceInfo.deviceTag);
    const selectedInstallment = computed(() => store.state.payOptions.selectedInstallment as IInstallmentDetail);
    const amount = computed(() => selectedInstallment.value?.amount);
    const term = computed(() => computeTermLabel(t, te, selectedInstallment.value?.term));

    const paymentOption = computed(() => store.state.payOptions.deviceInfo.paymentOption);
    const providers: {
      paymentProviders: Ref<UnwrapRef<IPaymentProvider[]>>;
      globalWarning: Ref<UnwrapRef<IGlobalWarning | null>>;
    } = usePaymentProviders({
      country: country.value,
      paymentOption: paymentOption.value,
    });
    const globalWarningDismissed = ref(false);

    const filteredProviders = computed(() => {
      let resultProviders: IPaymentProvider[] = [];

      // First let's check if the country is set and remove inactive providers
      if (country.value) {
        resultProviders = providers.paymentProviders.value.filter((provider) => provider.country === country.value);
      } else {
        resultProviders = providers.paymentProviders.value;
      }
      resultProviders = resultProviders.filter((provider) => !provider.showGrouped).sort((a, b) => a.order - b.order);

      let providersWithZeroFee = resultProviders.filter((provider) => provider.fee === 0);
      let providersWithNonZeroFee = resultProviders.filter((provider) => provider.fee !== 0);
      const superPacmanInstallment = store.state.payOptions.superPacmanInstallment;

      if (superPacmanInstallment) {
        if (country.value === Countries.MEXICO) {
          const cumulativeBalanceEnabled =
            superPacmanInstallment.cumulativeBalance?.enabled ??
            superPacmanInstallment.cumulativeBalance?.cumulativeBalanceEnabled ??
            false;
          const financeProductType = superPacmanInstallment.financeProduct.financeProductType;
          const isOldMxFo = financeProductType === FinanceProductTypes.FEv1 && !cumulativeBalanceEnabled;
          if (isOldMxFo) {
            // Old MX FO customers (FEv1 without cumulative balance): Show STP, hide BITSO
            providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.BITSO);
          } else {
            // All other MX customers (FEv2, or FEv1 with cumulative balance): Show BITSO, hide STP
            const bitsoExists = providersWithZeroFee.some((provider) => provider.name === Providers.BITSO);
            if (bitsoExists) {
              providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.STP);
            }
          }
        }
      } else {
        providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.BITSO);
      }

      if (superPacmanInstallment) {
        if (country.value === Countries.PERU) {
          const customerNationalIdType = superPacmanInstallment.financeProduct.customerNationalIdType;
          if (customerNationalIdType !== CustomerNationalIdTypes.NationalId) {
            // VPOP-459: Show Interbank for PE customers with National ID only
            providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.INTERBANK);
            providersWithNonZeroFee = providersWithNonZeroFee.filter(
              (provider) => provider.name !== Providers.INTERBANK
            );
          }
          if (
            ![CustomerNationalIdTypes.ResidencePermit, CustomerNationalIdTypes.NationalId].includes(
              customerNationalIdType
            )
          ) {
            // PYMT-1937: Show BCP and Kasnet only for PE customers with Residence Permit or National ID type
            providersWithZeroFee = providersWithZeroFee.filter(
              (provider) => provider.name !== Providers.BCP && provider.name !== Providers.KASNET
            );
            providersWithNonZeroFee = providersWithNonZeroFee.filter(
              (provider) => provider.name !== Providers.BCP && provider.name !== Providers.KASNET
            );
          }
        } else if (country.value === Countries.INDONESIA) {
          // VPOP-441: For Indonesia, filter providers based on subProductVersion
          const subProductVersion = superPacmanInstallment.financeProduct.subProductVersion;
          if (subProductVersion === "BSS") {
            providersWithZeroFee = providersWithZeroFee.filter(
              (provider) => provider.name === Providers.VIRTUAL_ACCOUNT
            );
            providersWithNonZeroFee = providersWithNonZeroFee.filter(
              (provider) => provider.name === Providers.VIRTUAL_ACCOUNT
            );
          } else if (subProductVersion === "Rental") {
            providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name === Providers.XENDIT);
            providersWithNonZeroFee = providersWithNonZeroFee.filter((provider) => provider.name === Providers.XENDIT);
          } else {
            providersWithZeroFee = [];
            providersWithNonZeroFee = [];
          }
        }
      }

      // PYMT-3452: Ecuador renders one list sorted by `order` (supersedes the
      // VPOP-426 in-store/online split) so configured order controls adjacency.
      if (country.value === Countries.ECUADOR) {
        providersWithZeroFee = resultProviders;
        providersWithNonZeroFee = [];
      }

      return [providersWithZeroFee, providersWithNonZeroFee];
    });

    const highlightedProviders = computed(() => {
      return providers.paymentProviders.value.filter((provider) => provider.highlight);
    });

    // for Brazil only, to highlight a Pix popup the first time user visits this page
    const pixProvider: Ref<IPaymentProvider | null> = computed(() => {
      if (country.value === Countries.BRAZIL) {
        let result: IPaymentProvider | null = providers.paymentProviders.value.filter(
          (provider) => provider.featureFlagStatus?.disabled !== true && provider.name === Providers.ASAAS_PIX
        )[0];

        if (!result) {
          // to ensure that if result is empty, pixProvider equals null
          result = null;
        }
        return result;
      } else {
        return null;
      }
    });

    const logoList = computed(() => {
      return Object.values(filteredProviders.value)
        .flat()
        .map((provider: IPaymentProvider) => provider.logo);
    });

    const detailsPopupObject = ref<IFeatureFlagLogType | undefined>();
    const pendingProvider = ref<IPaymentProvider | undefined>();
    const boletoPopupTitle = ref(t("Popup-Boleto-First-Time-Title"));
    const boletoPopupDescription = ref(t("Popup-Boleto-First-Time-Description"));
    const boletoPopupButtonText = ref(t("continue"));
    const dismissGlobalWarning = () => {
      globalWarningDismissed.value = true;
    };

    const showExtensionCta = computed(() => detailsPopupObject.value?.offer_extension === true);

    // PYMT-2651: country-level path. When the country warning is shown and the
    // country has offer_extension enabled, the global-warning popup surfaces its
    // own "Get an extension" CTA. Independent from the provider-level flag above.
    const globalWarningExtensionCta = computed(() => providers.globalWarning.value?.offer_extension === true);

    const allProvidersFeeFree = computed(() => {
      if (!country.value) return false;
      const countryProviders = providers.paymentProviders.value.filter((p) => p.country === country.value);
      return countryProviders.length > 0 && countryProviders.every((p) => p.fee === 0);
    });

    const computeRightText = (provider: IPaymentProvider): string => {
      // When the provider limit warning is showing, hide the right-side text
      // (e.g. "No fee") to free up room for the longer warning copy.
      if (getLimitWarning(provider)) return "";
      if (country.value === Countries.ECUADOR) {
        return provider.type === IPaymentProviderType.InStoreCash
          ? t("PaymentsProvidersView-InStorePayment")
          : t("PaymentsProvidersView-OnlinePayment");
      }
      if (provider.fee === 0) {
        return allProvidersFeeFree.value ? "" : t("no-fee");
      }
      return provider.toolTip ? t(provider.toolTip) : `+${provider.fee}`;
    };

    const isOtherAmountSelected = computed(() => store.state.payOptions.isOtherAmountSelected as boolean);
    const otherAmountAllowedProviders: string[] = [Providers.KASNET_QR, "PAYJOY"];
    // PYMT-1994: Indonesia Rental customers can pay any custom amount via Xendit,
    // so the "Other Amount" restriction popup should not block them.
    const isIndonesiaRental = computed(
      () =>
        country.value === Countries.INDONESIA &&
        store.state.payOptions.superPacmanInstallment?.financeProduct.subProductVersion === "Rental"
    );

    // `n` + `locale` from useI18n() are captured at setup time so the formatter
    // can run later (e.g. when invoked from tests) without an active instance.
    const formatLimitAmount = (amount: number): string =>
      n(amount, "currency", getFallbackLocale(store.state.payOptions.deviceInfo.country ?? locale.value)).replace(
        /\u00A0/g,
        ""
      );

    const getLimitWarning = (
      provider: IPaymentProvider
    ): { messageKey: string; messageParams: Record<string, string> } | null => {
      // Feature-flag messages take priority over the inline limit message.
      if (provider.featureFlagStatus?.disabled === true) return null;
      // Peru custom-amount restriction disables the provider entirely (only Punto PayJoy
      // and KasNet QR remain selectable), so suppress the limit warning on disabled ones.
      if (
        country.value === Countries.PERU &&
        isOtherAmountSelected.value &&
        !otherAmountAllowedProviders.includes(provider.name)
      ) {
        return null;
      }
      const amountToCheck = store.state.payOptions.superPacmanSelectedAmount ?? selectedInstallment.value?.amount ?? 0;
      const exceeded = getExceededProviderLimit(provider, amountToCheck);
      if (exceeded) {
        return {
          messageKey: exceeded.messageKey,
          messageParams: { limit: formatLimitAmount(exceeded.maxAmount) },
        };
      }
      const belowMinimum = getBelowMinimumProviderLimit(provider, amountToCheck);
      if (belowMinimum) {
        return {
          messageKey: belowMinimum.messageKey,
          messageParams: { limit: formatLimitAmount(belowMinimum.minAmount) },
        };
      }
      return null;
    };

    // Resolves the translated limit-warning copy for a provider, or an empty
    // string when no warning applies. Passed to CardItem's `warningText` prop
    // so the message renders inline below the provider's display name.
    const formatLimitWarning = (provider: IPaymentProvider): string => {
      const warning = getLimitWarning(provider);
      return warning ? t(warning.messageKey, warning.messageParams) : "";
    };

    const tokuUrl = computed((): string | null => {
      if (!isUIFlagEnabled(store.state.payOptions.uiFlags, "toku_autodebit_banner")) return null;
      const fp = store.state.payOptions.superPacmanInstallment?.financeProduct;
      if (!fp) return null;
      const allowedTypes: string[] = [FinanceProductTypes.FEv1, FinanceProductTypes.JointLoanLine];
      if (!allowedTypes.includes(fp.financeProductType)) return null;
      return buildTokuUrl({
        customerUserId: fp.customerUserId,
        deviceTag: fp.deviceTag,
        financeProductType: fp.financeProductType,
        financeProductId: fp.financeProductId,
      });
    });

    const useRedesign = computed(() =>
      isUIFlagEnabled(store.state.payOptions.uiFlags, PAYMENT_FLOW_REDESIGN_FLAG)
    );
    const superPacmanInstallment = computed(() => store.state.payOptions.superPacmanInstallment);
    const selectedProviderKey = ref<string | null>(null);
    const limitPopupProvider = ref<IPaymentProvider | undefined>();

    const redesignAmountValue = computed(
      () => store.state.payOptions.superPacmanSelectedAmount ?? selectedInstallment.value?.amount ?? 0
    );
    const redesignAmountDisplay = computed(() => formatLimitAmount(redesignAmountValue.value));
    const redesignNextPayment = computed<string>(() => {
      const sp = superPacmanInstallment.value;
      if (!sp) return "";
      const ms = nextValidThroughMs(sp.daysToAmount, redesignAmountValue.value);
      if (ms === null) return "";
      const date = d(ms, "medium", getFallbackLocale(store.state.payOptions.deviceInfo.country ?? locale.value));
      return t("superpacman-next-payment-date", { date });
    });

    const isProviderDimmed = (provider: IPaymentProvider): boolean =>
      provider.featureFlagStatus?.disabled === true ||
      (country.value === Countries.PERU &&
        isOtherAmountSelected.value &&
        !otherAmountAllowedProviders.includes(provider.name)) ||
      !!getLimitWarning(provider);

    const redesignCardSubtitle = (provider: IPaymentProvider): string | undefined => {
      if (provider.subtitle) return t(provider.subtitle);
      if (provider.tooltipPrefix) return t(provider.tooltipPrefix);
      const rightText = computeRightText(provider);
      return rightText || undefined;
    };

    const redesignCardLogo = (provider: IPaymentProvider): string => {
      if (provider.type === IPaymentProviderType.Group) return cashTile;
      if (provider.type === IPaymentProviderType.BankTransfer) return bankTransferTile;
      if (provider.name === "PAYJOY") return payjoyStoresTile;
      return provider.logo;
    };

    const groupCardSubtitle = (provider: IPaymentProvider): string | undefined =>
      provider.groupSectionSubtitle ? t(provider.groupSectionSubtitle) : undefined;

    const redesignProviderKeyOf = computed(() => providerKeyBuilder(filteredProviders.value.flat()));
    const redesignProviderKey = (provider: IPaymentProvider): string => redesignProviderKeyOf.value(provider);

    const makeRedesignCardItem = (provider: IPaymentProvider): ProviderCardItem => {
      const isGroup = provider.type === IPaymentProviderType.Group;
      return {
        key: redesignProviderKey(provider),
        title: t(isGroup && provider.groupSectionTitle ? provider.groupSectionTitle : provider.displayName),
        subtitle: isGroup ? groupCardSubtitle(provider) : redesignCardSubtitle(provider),
        logoUrl: redesignCardLogo(provider),
        dimmed: isProviderDimmed(provider),
        testId: isGroup ? `sp-provider-group-${provider.name}` : `sp-provider-${provider.name}`,
      };
    };

    const redesignCardItems = computed<ProviderCardItem[]>(() => {
      const [, providersWithFee] = filteredProviders.value;
      const providersByOrder = filteredProviders.value.flat().sort((a, b) => a.order - b.order);
      const items: ProviderCardItem[] = providersByOrder.map(makeRedesignCardItem);
      if (tokuUrl.value) {
        const firstWithFee = providersByOrder.findIndex((provider) => providersWithFee.includes(provider));
        items.splice(firstWithFee === -1 ? items.length : firstWithFee, 0, {
          key: TOKU_CARD_KEY,
          title: t("TokuAutodebit-Channel-Title"),
          subtitle: t("no-fee"),
          logoUrl: addCardIcon,
          dimmed: false,
          testId: "toku-autodebit-channel",
        });
      }
      return items;
    });

    const redesignProviderMap = computed<Record<string, IPaymentProvider>>(() => {
      const map: Record<string, IPaymentProvider> = {};
      filteredProviders.value.flat().forEach((provider) => {
        map[redesignProviderKey(provider)] = provider;
      });
      return map;
    });

    const canContinueRedesign = computed<boolean>(() => selectedProviderKey.value !== null);

    const limitPopupAmountToCheck = computed<number>(
      () => store.state.payOptions.superPacmanSelectedAmount ?? selectedInstallment.value?.amount ?? 0
    );

    const limitPopupIsBelowMinimum = computed<boolean>(() => {
      const provider = limitPopupProvider.value;
      if (!provider) return false;
      return (
        getExceededProviderLimit(provider, limitPopupAmountToCheck.value) === null &&
        getBelowMinimumProviderLimit(provider, limitPopupAmountToCheck.value) !== null
      );
    });

    const limitPopupTitle = computed<string>(() =>
      t(limitPopupIsBelowMinimum.value ? "availability-min-amount-title" : "availability-max-amount-title")
    );

    const limitPopupBody = computed<string>(() => {
      const provider = limitPopupProvider.value;
      if (!provider) return "";
      const exceeded = getExceededProviderLimit(provider, limitPopupAmountToCheck.value);
      if (exceeded) {
        return t("availability-max-amount-body", { limit: formatLimitAmount(exceeded.maxAmount) });
      }
      const belowMinimum = getBelowMinimumProviderLimit(provider, limitPopupAmountToCheck.value);
      if (belowMinimum) {
        return t("availability-min-amount-body", { limit: formatLimitAmount(belowMinimum.minAmount) });
      }
      return "";
    });

    return {
      t,
      addCardIcon,
      useRedesign,
      superPacmanInstallment,
      selectedProviderKey,
      limitPopupProvider,
      redesignAmountDisplay,
      redesignNextPayment,
      redesignCardItems,
      redesignProviderMap,
      redesignProviderKey,
      canContinueRedesign,
      limitPopupTitle,
      limitPopupBody,
      providers,
      filteredProviders,
      highlightedProviders,
      router,
      country,
      deviceTag,
      amount,
      globalWarningDismissed,
      dismissGlobalWarning,
      selectedInstallment,
      paymentOption,
      pixProvider,
      logoList,
      detailsPopupObject,
      pendingProvider,
      showExtensionCta,
      globalWarningExtensionCta,
      term,
      store,
      computeRightText,
      boletoPopupTitle,
      boletoPopupDescription,
      boletoPopupButtonText,
      Providers,
      isOtherAmountSelected,
      otherAmountAllowedProviders,
      isIndonesiaRental,
      getLimitWarning,
      formatLimitWarning,
      tokuUrl,
      openTokuUrl: () => {
        if (tokuUrl.value) window.open(tokuUrl.value, "_blank", "noopener,noreferrer");
      },
    };
  },
  data() {
    return {
      onHighlighted: false,
      showWarningPopUp: false,
      showBoletoPopup: false,
      showOtherAmountPopup: false,
    };
  },
  watch: {
    logoList: {
      handler(logoList) {
        if (logoList.length > 0) prefetchHelper.prefetchImages(logoList);
      },
      immediate: true,
    },
  },
  async mounted() {
    // Check if the user has visited this page before
    const hasVisited = getCookie("hasVisitedPaymentProviders");
    if (hasVisited) {
      this.onHighlighted = false;
    } else {
      setCookie("hasVisitedPaymentProviders", "true", 365);

      if ([Countries.INDONESIA, Countries.PERU].includes(this.country)) {
        this.onHighlighted = false;
      }

      // the pay without commissions popup should work only for MX
      this.onHighlighted = this.country === Countries.MEXICO;
    }

    if (this.isOtherAmountSelected && this.country === Countries.PERU) {
      this.showOtherAmountPopup = true;
    }

    const flags = await authHelper.getUIFlags();
    this.store.dispatch("payOptions/setUIFlags", flags);

    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page view
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnSelectPaymentProviderPage,
          actionType: ActionLogTypeConst.PAGE_VIEW,
        },
        this.$gtag
      );
    }

    // Is on highlight PopUp? log the event
    if (this.onHighlighted) {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_CommissionsModalOpened,
            actionType: ActionLogTypeConst.PAGE_VIEW,
          },
          this.$gtag
        );
      }
    }
  },
  beforeUnmount() {
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page unmount
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnSelectPaymentProviderPage_BrowserClose,
          actionType: ActionLogTypeConst.PAGE_VIEW,
        },
        this.$gtag
      );
    }

    this.router.beforeEach((to, from, next) => {
      if (to.name === "InstallmentsByDevice") {
        if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
          //Go back to InstallmentsByDevice, so log the action
          authHelper.logAction(
            {
              deviceTag: this.deviceTag,
              country: this.country,
              action: AnalyticEventsNames.OnSelectPaymentProviderPage_BrowserGoBack,
              actionType: ActionLogTypeConst.BUTTON_CLICK,
            },
            this.$gtag
          );
        }
      }
      next();
    });
  },
  methods: {
    handleSelectPaymentProvider(selectedPaymentProvider: IPaymentProvider) {
      if (
        this.country === Countries.PERU &&
        this.isOtherAmountSelected &&
        !this.otherAmountAllowedProviders.includes(selectedPaymentProvider.name)
      ) {
        this.showOtherAmountPopup = true;
        return;
      }
      // Feature-flag status is evaluated before the provider limit so backend
      // messages (popup or silent block) always take priority over the inline
      // limit warning.
      const { disabled, title, banner } = selectedPaymentProvider.featureFlagStatus ?? {};
      if (disabled === true && !title && !banner) {
        // PYMT-2651: a disabled provider with no backend message is normally a
        // silent no-op. When the provider itself offers extensions, still open
        // the popup with fallback copy so the "Get an extension" CTA is reachable.
        if (selectedPaymentProvider.featureFlagStatus?.offer_extension === true) {
          this.detailsPopupObject = {
            ...selectedPaymentProvider.featureFlagStatus,
            title: this.t("method-not-available-title"),
            banner: this.t("method-not-available-description"),
          };
          this.pendingProvider = selectedPaymentProvider;
        }
        return;
      }
      if (title || banner) {
        this.detailsPopupObject = selectedPaymentProvider.featureFlagStatus;
        this.pendingProvider = selectedPaymentProvider;
        return;
      }
      const amountToCheck =
        this.store.state.payOptions.superPacmanSelectedAmount ?? this.selectedInstallment?.amount ?? 0;
      if (
        getExceededProviderLimit(selectedPaymentProvider, amountToCheck) !== null ||
        getBelowMinimumProviderLimit(selectedPaymentProvider, amountToCheck) !== null
      ) {
        return;
      }
      this.proceedWithProvider(selectedPaymentProvider);
    },
    handleRedesignSelect(key: string) {
      if (key === TOKU_CARD_KEY) {
        this.openTokuUrl();
        return;
      }
      const provider = this.redesignProviderMap[key];
      if (!provider) return;
      if (
        this.country === Countries.PERU &&
        this.isOtherAmountSelected &&
        !this.otherAmountAllowedProviders.includes(provider.name)
      ) {
        this.showOtherAmountPopup = true;
        return;
      }
      const { disabled, title, banner } = provider.featureFlagStatus ?? {};
      if (disabled === true) {
        if (title || banner) {
          this.detailsPopupObject = provider.featureFlagStatus;
          this.pendingProvider = provider;
        } else if (provider.featureFlagStatus?.offer_extension === true) {
          this.detailsPopupObject = {
            ...provider.featureFlagStatus,
            title: this.t("method-not-available-title"),
            banner: this.t("method-not-available-description"),
          };
          this.pendingProvider = provider;
        }
        return;
      }
      const amountToCheck =
        this.store.state.payOptions.superPacmanSelectedAmount ?? this.selectedInstallment?.amount ?? 0;
      if (
        getExceededProviderLimit(provider, amountToCheck) !== null ||
        getBelowMinimumProviderLimit(provider, amountToCheck) !== null
      ) {
        this.limitPopupProvider = provider;
        return;
      }
      if (title || banner) {
        this.detailsPopupObject = provider.featureFlagStatus;
        this.pendingProvider = provider;
        return;
      }
      this.selectedProviderKey = key;
    },
    onRedesignContinue() {
      const provider = this.selectedProviderKey ? this.redesignProviderMap[this.selectedProviderKey] : undefined;
      if (provider) this.proceedWithProvider(provider);
    },
    onRedesignDetailsPrimary() {
      const object = this.detailsPopupObject;
      const provider = this.pendingProvider;
      this.detailsPopupObject = undefined;
      this.pendingProvider = undefined;
      if (provider && object?.disabled !== true) {
        this.selectedProviderKey = this.redesignProviderKey(provider);
      }
    },
    onRedesignLimitClose() {
      this.limitPopupProvider = undefined;
    },
    proceedWithProvider(selectedPaymentProvider: IPaymentProvider) {
      this.$emit("select-payment-provider", selectedPaymentProvider.name);

      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_SelectPaymentProvider,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
            data: {
              deviceTag: this.deviceTag,
              country: this.country,
              provider: selectedPaymentProvider.name,
            },
          },
          this.$gtag
        );
      }

      // In Brazil, Boleto still shows a confirmation modal. PIX now goes straight to the
      // reference page (PYMT-2573: the intermediate 10-minute popup was removed to cut a screen).
      if (this.country === Countries.BRAZIL && selectedPaymentProvider.name === Providers.ASAAS_BOLETO) {
        this.showBoletoPopup = true;
        return;
      }

      router.push({
        name: "DynamicProviderLoader",
        params: {
          providerName: selectedPaymentProvider.name,
          displayName: selectedPaymentProvider.displayName,
          type: selectedPaymentProvider.type,
        },
      });
    },
    handleAmountChange() {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_ChangeInstallment,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
          },
          this.$gtag
        );
      }
      if (this.store.state.payOptions.superPacmanInstallment) {
        this.router.push("/p/device/" + this.deviceTag + "?showoptions=" + this.paymentOption + "&forceUpdate=true");
      } else {
        this.router.push("/device/" + this.deviceTag + "?showoptions=" + this.paymentOption + "&forceUpdate=true");
      }
    },
    onDismissPopup() {
      this.detailsPopupObject = undefined;
      this.showBoletoPopup = false;
      this.showOtherAmountPopup = false;
    },
    onDetailsBottomLink() {
      // Secondary button of the disabled-provider popup: routes to the
      // Extensions flow when the offer-extension CTA is active, otherwise it is
      // the plain "cancel" link that just dismisses the popup.
      if (this.showExtensionCta) {
        this.onRequestExtension();
      } else {
        this.onDismissPopup();
      }
    },
    onRequestExtension() {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_GetExtension,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
          },
          this.$gtag
        );
      }
      this.detailsPopupObject = undefined;
      this.pendingProvider = undefined;
      router.push({ name: "ExtensionTypeView", params: { deviceTag: this.deviceTag } });
    },
    onGlobalWarningExtension() {
      // Secondary CTA of the country-level global-warning popup: routes to the
      // Extensions flow when the country has offer_extension enabled.
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_GetExtension,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
          },
          this.$gtag
        );
      }
      this.dismissGlobalWarning();
      router.push({ name: "ExtensionTypeView", params: { deviceTag: this.deviceTag } });
    },
    onClosePopup(source: string) {
      this.$emit("close-popup");

      switch (source) {
        case "details":
          this.detailsPopupObject = undefined;
          const provider = this.pendingProvider;
          this.pendingProvider = undefined;
          if (provider && provider.featureFlagStatus?.disabled !== true) {
            this.proceedWithProvider(provider);
          }
          break;

        case "highLight":
          this.onHighlighted = false;
          if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
            authHelper.logAction(
              {
                deviceTag: this.deviceTag,
                country: this.country,
                action: AnalyticEventsNames.OnSelectPaymentProviderPage_CloseCommissionsModal,
                actionType: ActionLogTypeConst.BUTTON_CLICK,
              },
              this.$gtag
            );
          }
          break;

        case "boletoPopup":
          this.showBoletoPopup = false;

          // customers can pay with Boleto, only if the chosen amount >= R$5
          if (this.amount >= 5) {
            router.push({
              name: "DynamicProviderLoader",
              params: { providerName: Providers.ASAAS_BOLETO },
            });
          } else if (this.boletoPopupButtonText === this.t("close")) {
            // reset the Boleto modal to it's initial content
            this.boletoPopupTitle = this.t("Popup-Boleto-First-Time-Title");
            this.boletoPopupDescription = this.t("Popup-Boleto-First-Time-Description");
            this.boletoPopupButtonText = this.t("continue");
          } else {
            // if amount is less than R$5, the customer cannot pay with Boleto. Must pay with PIX
            this.boletoPopupTitle = this.t("Popup-Boleto-R5-Title");
            this.boletoPopupDescription = this.t("Popup-Boleto-R5-Description");
            this.boletoPopupButtonText = this.t("close");

            setTimeout(() => {
              this.showBoletoPopup = true;
            }, 100);
          }
          break;

        default:
          this.showWarningPopUp = false;
          this.detailsPopupObject = undefined;
          break;
      }
    },
    tryPix() {
      // PYMT-2573: navigate straight to the PIX reference page (10-minute popup removed).
      this.showBoletoPopup = false;
      router.push({
        name: "DynamicProviderLoader",
        params: { providerName: Providers.ASAAS_PIX },
      });
    },
  },
});
</script>

<style scoped>
.sp-providers__title {
  margin: 32px 0 24px;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.providers-list {
  padding: 16px 16px 128px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.providers-list h1 {
  color: #333;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.26px;
  margin: 0;
}

h3 {
  margin: 16px 0 0 0;
}

.providers-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.providers-list ul > li {
  position: relative;
}


.message h1 {
  color: var(--Primary-Green-1);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px; /* 120% */
}

.message h2 {
  color: var(--Typography-Text-2);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}

.savings-container {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  max-width: 230px;
  margin-left: auto;
  margin-right: auto;
  list-style: none;
  padding: 0;
}

.savings {
  background: #fff;
  display: flex;
}

.savings img {
  margin: auto;
  min-width: 50px;
  max-width: 70px;
}
</style>
